import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, GripVertical, Settings } from 'lucide-react';
import { MatterType, MatterTypeField, db } from '../../lib/db';
import { toast } from 'sonner';

interface MatterTypeFieldManagerProps {
  matterType: MatterType;
  onClose: () => void;
}

const fieldTypeOptions = [
  { value: 'text', label: 'Text', description: 'Single line text' },
  { value: 'multiline', label: 'Multi-line Text', description: 'Multiple lines of text' },
  { value: 'number', label: 'Number', description: 'Numeric values' },
  { value: 'date', label: 'Date', description: 'Date picker' },
  { value: 'boolean', label: 'Yes/No', description: 'Checkbox for true/false' },
  { value: 'email', label: 'Email', description: 'Email address' },
  { value: 'url', label: 'URL', description: 'Website link' },
  { value: 'select', label: 'Dropdown', description: 'Select from options' },
];

export function MatterTypeFieldManager({ matterType, onClose }: MatterTypeFieldManagerProps) {
  const [fields, setFields] = useState<MatterTypeField[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingField, setEditingField] = useState<MatterTypeField | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    field_type: 'text' as MatterTypeField['field_type'],
    description: '',
    is_required: false,
    options: [] as string[],
    newOption: '',
  });

  useEffect(() => {
    loadFields();
  }, [matterType.id]);

  const loadFields = async () => {
    try {
      const loadedFields = await db.matterTypeFields.findByMatterTypeId(matterType.id);
      setFields(loadedFields);
    } catch (error) {
      console.error('Error loading fields:', error);
      toast.error('Failed to load fields');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a field name');
      return;
    }

    if (formData.field_type === 'select' && formData.options.length === 0) {
      toast.error('Please add at least one option for the dropdown');
      return;
    }

    try {
      const fieldData = {
        matter_type_id: matterType.id,
        name: formData.name.trim(),
        field_type: formData.field_type,
        description: formData.description.trim() || undefined,
        is_required: formData.is_required,
        position: editingField ? editingField.position : fields.length,
        options: formData.field_type === 'select' ? formData.options : undefined,
      };

      if (editingField) {
        await db.matterTypeFields.update(editingField.id, fieldData);
        toast.success('Field updated successfully');
      } else {
        await db.matterTypeFields.create(fieldData);
        toast.success('Field created successfully');
      }
      
      resetForm();
      loadFields();
    } catch (error) {
      console.error('Error saving field:', error);
      toast.error('Failed to save field');
    }
  };

  const handleDelete = async (fieldId: string) => {
    if (!confirm('Are you sure you want to delete this field? All data for this field will be lost.')) {
      return;
    }

    try {
      await db.matterTypeFields.delete(fieldId);
      toast.success('Field deleted successfully');
      loadFields();
    } catch (error) {
      console.error('Error deleting field:', error);
      toast.error('Failed to delete field');
    }
  };

  const handleAddOption = () => {
    if (formData.newOption.trim()) {
      setFormData({
        ...formData,
        options: [...formData.options, formData.newOption.trim()],
        newOption: '',
      });
    }
  };

  const handleRemoveOption = (index: number) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index),
    });
  };

  const startEdit = (field: MatterTypeField) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      field_type: field.field_type,
      description: field.description || '',
      is_required: field.is_required || false,
      options: field.options || [],
      newOption: '',
    });
    setIsCreating(true);
  };

  const resetForm = () => {
    setIsCreating(false);
    setEditingField(null);
    setFormData({
      name: '',
      field_type: 'text',
      description: '',
      is_required: false,
      options: [],
      newOption: '',
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Manage Fields for "{matterType.name}"
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Define custom fields to collect specific information for this matter type
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {!isCreating && (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full mb-6 flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Field
            </button>
          )}

          {isCreating && (
            <form onSubmit={handleSubmit} className="mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Field Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    placeholder="e.g., Case Number, Budget, Client Contact"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="field_type" className="block text-sm font-medium text-gray-700">
                    Field Type
                  </label>
                  <select
                    id="field_type"
                    value={formData.field_type}
                    onChange={(e) => setFormData({ ...formData, field_type: e.target.value as MatterTypeField['field_type'] })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                  >
                    {fieldTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} - {option.description}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.field_type === 'select' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dropdown Options
                    </label>
                    <div className="space-y-2">
                      {formData.options.map((option, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="flex-1 px-3 py-1 bg-white border border-gray-300 rounded-md text-sm">
                            {option}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveOption(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.newOption}
                          onChange={(e) => setFormData({ ...formData, newOption: e.target.value })}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          placeholder="Add option"
                        />
                        <button
                          type="button"
                          onClick={handleAddOption}
                          className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description (Optional)
                  </label>
                  <input
                    type="text"
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    placeholder="Help text for this field"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_required"
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_required" className="ml-2 block text-sm text-gray-700">
                    Required field
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
                  >
                    {editingField ? 'Update' : 'Create'} Field
                  </button>
                </div>
              </div>
            </form>
          )}

          <div className="space-y-2">
            {fields.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No custom fields defined yet. Add fields to collect specific information for this matter type.
              </div>
            ) : (
              fields.map((field) => (
                <div
                  key={field.id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">{field.name}</h4>
                        {field.is_required && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                            Required
                          </span>
                        )}
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                          {fieldTypeOptions.find(t => t.value === field.field_type)?.label}
                        </span>
                      </div>
                      {field.description && (
                        <p className="text-sm text-gray-600 mt-1">{field.description}</p>
                      )}
                      {field.field_type === 'select' && field.options && (
                        <p className="text-xs text-gray-500 mt-1">
                          Options: {field.options.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => startEdit(field)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit field"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(field.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete field"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}